import { useState, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Loader2, CreditCard, Lock } from "lucide-react";

const PAYFAST_ONSITE_SCRIPT_SANDBOX =
  "https://sandbox.payfast.co.za/onsite/engine.js";
const PAYFAST_ONSITE_SCRIPT_PRODUCTION =
  "https://www.payfast.co.za/onsite/engine.js";

export default function PaymentForm({
  uuid,
  amount,
  currency = "ZAR",
  onSuccess,
  onError,
  isProcessing,
  setIsProcessing,
  onPayfastReady,
  onModalClose,
}) {
  const [scriptLoaded, setScriptLoaded] = useState(false);
  const [scriptError, setScriptError] = useState(null);

  const isSandbox = import.meta.env.VITE_PAYFAST_SANDBOX === "true" || import.meta.env.DEV;

  // Load PayFast onsite script
  useEffect(() => {
    const scriptUrl = isSandbox
      ? PAYFAST_ONSITE_SCRIPT_SANDBOX
      : PAYFAST_ONSITE_SCRIPT_PRODUCTION;

    // Check if script is already loaded
    if (window.payfast_do_onsite_payment) {
      setScriptLoaded(true);
      return;
    }

    const existingScript = document.querySelector(
      `script[src="${scriptUrl}"]`
    );
    if (existingScript) {
      setScriptLoaded(true);
      return;
    }

    const script = document.createElement("script");
    script.src = scriptUrl;
    script.async = true;
    script.onload = () => setScriptLoaded(true);
    script.onerror = () =>
      setScriptError("Failed to load PayFast payment script");
    document.body.appendChild(script);

    return () => {
      // Don't remove script on unmount - it may be needed again
    };
  }, [isSandbox]);

  const handlePay = useCallback(() => {
    if (!uuid || !window.payfast_do_onsite_payment) {
      onError?.("Payment not initialized. Please try again.");
      return;
    }

    setIsProcessing(true);

    // Watch for PayFast's iframe, then hide our modal once it's loaded
    const observer = new MutationObserver(() => {
      const iframe = document.querySelector("iframe[src*='payfast']");
      if (iframe) {
        observer.disconnect();
        iframe.addEventListener("load", () => onPayfastReady?.());
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });

    window.payfast_do_onsite_payment(
      { uuid },
      (result) => {
        observer.disconnect();
        onModalClose?.();

        if (result === true) {
          onSuccess?.();
        } else {
          setIsProcessing(false);
          onError?.("Payment was not completed. Please try again.");
        }
      }
    );
  }, [uuid, onSuccess, onError, setIsProcessing, onPayfastReady, onModalClose]);

  const formatAmount = (amount, currency) => {
    const formatter = new Intl.NumberFormat("en-ZA", {
      style: "currency",
      currency: currency.toUpperCase(),
      minimumFractionDigits: 2,
    });
    return formatter.format(amount / 100); // Convert from cents
  };

  if (scriptError) {
    return (
      <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
        <p className="text-sm text-red-800">{scriptError}</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Amount display */}
      <div className="bg-slate-50 p-4 rounded-lg">
        <div className="flex justify-between items-center">
          <span className="text-slate-600">Amount to pay</span>
          <span className="text-2xl font-bold text-slate-900">
            {formatAmount(amount, currency)}
          </span>
        </div>
      </div>

      {/* Security note */}
      <div className="flex items-center gap-2 text-sm text-slate-500">
        <Lock className="w-4 h-4" />
        <span>Your payment is secure and processed by PayFast</span>
      </div>

      {/* Pay button */}
      <Button
        type="button"
        onClick={handlePay}
        disabled={!scriptLoaded || isProcessing || !uuid}
        className="w-full"
        size="lg"
      >
        {!scriptLoaded ? (
          <>
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            Loading payment...
          </>
        ) : isProcessing ? (
          <>
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            Processing payment...
          </>
        ) : (
          <>
            <CreditCard className="w-4 h-4 mr-2" />
            Pay {formatAmount(amount, currency)}
          </>
        )}
      </Button>

      {/* Sandbox test note */}
      {isSandbox && (
        <div className="bg-amber-50 border border-amber-200 p-3 rounded-lg">
          <p className="text-sm text-amber-800">
            <span className="font-medium">Sandbox mode:</span> Use test buyer
            credentials:{" "}
            <code className="bg-amber-100 px-1 rounded">
              sbtu01@payfast.io
            </code>{" "}
            / <code className="bg-amber-100 px-1 rounded">clientpass</code>
          </p>
        </div>
      )}
    </div>
  );
}
